/**
 * widgets/registry.docker.tsx — Docker / Kontejnery (2.1 widget-first).
 *
 * ## Jak se volá (prezentační vrstva)
 * UI NIKDY nevolá Docker CLI ani Deno přímo. Jen řekne „tool + args“:
 *
 *   callWidgetTool('docker.container_*', args, scope)
 *     → mcpCall  POST separate MCP service URL (session cookie + proxy ACL)
 *     → UI adapter → python MCP proxy 127.0.0.1:8092
 *
 * Kanonická jména toolů: docker.container_list | logs | inspect | action | create
 * (underscore alias docker_container_* pořád funguje přes registry aliases).
 *
 * PIN se vrací pouze jednou při vytvoření zámku. Browser jej později nečte
 * z DB; odemčení je scopeovaný owner tool a audit nikdy neukládá hodnotu PINu.
 *
 * UI: design system — SearchInput, Button, StatusDot, Badge, EmptyState, StatCard.
 * Sdílený ContainerList / ModuleContainerList — plná stránka i patička modulů.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import {
  Box,
  CheckCircle2,
  ChevronDown,
  ChevronRight,
  Clock,
  Copy,
  Cpu,
  Download,
  FileJson,
  HardDrive,
  Image as ImageIcon,
  Layers,
  Loader2,
  Lock as LockIcon,
  MemoryStick,
  Network,
  Play,
  Plus,
  RotateCw,
  ScrollText,
  Square,
  Trash2,
  Unlock,
} from 'lucide-react';
import {
  Badge,
  Button,
  EmptyState,
  Input,
  Modal,
  RefreshButton,
  SearchInput,
  StatCard,
  StatsGrid,
  StatusDot,
} from '../components/ui';
import { mcpCall } from '../lib/mcp';
import { useWidgetData } from './hooks';
import { callWidgetTool } from './scope';
import type { EffectiveScope, WidgetRenderProps, WidgetSpec } from './types';
import { themedConfirm } from '../lib/themed-dialogs';
import i18n from '../lib/i18n';

// ── MCP tools (kanonická jména — python proxy aliasuje i underscore) ─────────

const T = {
  list: 'docker.container_list',
  logs: 'docker.container_logs',
  inspect: 'docker.container_inspect',
  action: 'docker.container_action',
  create: 'docker.container_create',
  imageList: 'docker.image_list',
  imageAction: 'docker.image_action',
  volumeList: 'docker.volume_list',
  volumeAction: 'docker.volume_action',
  networkList: 'docker.network_list',
  lock: 'toolbox.production_lock',
} as const;

// ── types ────────────────────────────────────────────────────────────────────

interface ContainerStats {
  cpu: string;
  mem: string;
  mem_perc: string;
}
interface ContainerRow {
  id: string;
  name: string;
  image: string;
  status: string;
  state: string;
  ports: string;
  created: string;
  module?: string;
  /** acm.deployment — čitelný název projektu / env (popisek, NE identita) */
  deployment?: string;
  /** acm.project — IDENTITA projektu (PRJ-XXXXXXXX). Podle ní se seskupuje. */
  project_id?: string;
  /** acm.instance — instance uvnitř modulu (volitelné) */
  instance?: string;
  service?: string;
  acm_managed?: boolean;
  stats?: ContainerStats | null;
}

/** Hierarchie: modul → projekt (podle acm.project) → kontejnery */
interface ProjectGroup {
  projectKey: string;
  projectLabel: string;
  rows: ContainerRow[];
}
interface ModuleGroup {
  moduleKey: string;
  moduleLabel: string;
  projects: ProjectGroup[];
  total: number;
  running: number;
}
interface ListResp {
  ok?: boolean;
  total?: number;
  containers?: ContainerRow[];
  error?: string;
}
type CAction = 'start' | 'stop' | 'restart' | 'remove';
type FilterMode = 'all' | 'running' | 'stopped';
/** Jak seskupit seznam: modul→projekt, projekt (orchestrátor), flat. */
type GroupMode = 'module' | 'project' | 'flat';

const clean = (n: string) => n.replace(/^\//, '');

function matchesModuleName(cname: string, mod: string): boolean {
  const n = clean(cname);
  return n.startsWith(`${mod}-`) || n.startsWith(`acm-${mod}-`) || n === mod;
}

/** Patří kontejner modulu? Label acm.module, jinak konvence jména. */
function belongsToModule(c: { name: string; module?: string }, mod: string): boolean {
  if (c.module === mod) return true;
  return matchesModuleName(c.name, mod);
}

/**
 * Projekt jak ho vnímá orchestrátor / ACM labely:
 *  1. acm.project — IDENTITA. Jediné spolehlivé seskupení.
 *  2. acm.deployment (název projektu z orch) — popisek, recykluje se
 *  3. acm.instance bez service suffixů (-backend/-frontend/…)
 *  4. heuristika z jména kontejneru
 *
 * Body 2–4 jsou dohady podle jména a zůstávají jen pro kontejnery vytvořené před
 * zavedením `acm.project` (2026-08-12). Dokud existují, může seskupení splést dva
 * projekty stejného názvu do jedné karty — proto UI u nich ukazuje název, ale
 * `projectHasIdentity` říká, že vazba je odhad.
 */
function resolveProjectKey(c: ContainerRow): string {
  const pid = c.project_id?.trim();
  if (pid) return pid;
  const dep = c.deployment?.trim();
  if (dep) return dep;
  const inst = c.instance?.trim();
  if (inst) {
    return inst
      .replace(/-(backend|frontend|api|app|db|redis|worker|studio|meta|storage|pooler|auth|rest|realtime)(-dev)?$/i, '')
      .replace(/-supabase$/i, '');
  }
  const n = clean(c.name);
  let m = n.match(/^apphost-(.+?)-(?:frontend|backend)(?:-dev)?-app$/);
  if (m?.[1]) return m[1];
  m = n.match(/^(.+?)-supabase-/);
  if (m?.[1]) return m[1].replace(/-backend$/, '');
  m = n.match(/^(?:dbhost|webhost)-(.+)$/);
  if (m?.[1]) return m[1];
  return '_other';
}

/**
 * Popisek skupiny projektu. Seskupuje se podle `acm.project` (ID), ale člověku se
 * ukazuje název z `acm.deployment` — ID je identita, ne text pro čtení. Když název
 * chybí, zbyde ID (lepší než „ostatní").
 */
function projectGroupLabel(key: string, rows: ContainerRow[]): string {
  if (key === '_other') return i18n.t('containers.noProject', 'Bez projektu');
  const named = rows.find((r) => r.deployment?.trim())?.deployment?.trim();
  return named ?? key;
}

/** Nese skupina skutečnou identitu, nebo je slepená podle jména? */
function projectHasIdentity(rows: ContainerRow[]): boolean {
  return rows.some((r) => !!r.project_id?.trim());
}

function downloadText(filename: string, text: string, mime = 'application/json') {
  const blob = new Blob([text], { type: mime });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    return false;
  }
}

/**
 * Sdílený seznam kontejnerů — používá:
 *  - globální /core/containers (controls)
 *  - patička každé modulové stránky (moduleFilter + compact)
 *  - případně vlastní embed v kartách (projectFilter)
 */
export interface ContainerListProps {
  scope: EffectiveScope;
  /** Filtr ACM modulu (acm.module + name fallback). */
  moduleFilter?: string;
  /** Filtr projektu (acm.deployment). */
  projectFilter?: string;
  /** @deprecated Použij moduleFilter — legacy name-prefix filtr. */
  prefixFilter?: string;
  /**
   * Plný toolbar (stats, filtry, deploy, group toggle).
   * Default: true když není moduleFilter/compact.
   */
  controls?: boolean;
  /**
   * Embed režim (modulová patička / md widget):
   * menší chrome, bez deploy, auto-group podle projektu.
   */
  compact?: boolean;
  /** Skryj celý blok, když po filtrech nic nezůstane (modul bez kontejnerů). */
  hideWhenEmpty?: boolean;
  /** Nadpis v compact hlavičce. */
  title?: string;
  className?: string;
}

/** Ohraničené ikonové tlačítko — stejný vzor jako stará Containers.tsx. */
function IconBtn({
  icon,
  title,
  onClick,
  variant = 'muted',
  disabled,
}: {
  icon: React.ReactNode;
  title: string;
  onClick: () => void;
  variant?: 'muted' | 'success' | 'warning' | 'danger' | 'info' | 'amber';
  disabled?: boolean;
}) {
  const cls: Record<string, string> = {
    muted: 'bg-gray-700/30 border-gray-700 text-gray-400 hover:text-white hover:bg-gray-700/50',
    success: 'bg-green-500/10 border-green-500/20 text-green-400 hover:bg-green-500/20',
    warning: 'bg-amber-500/10 border-amber-500/20 text-amber-400 hover:bg-amber-500/20',
    danger: 'bg-red-500/10 border-red-500/20 text-red-400 hover:bg-red-500/20',
    info: 'bg-blue-500/10 border-blue-500/20 text-blue-400 hover:bg-blue-500/20',
    amber: 'bg-amber-500/10 border-amber-500/20 text-amber-400 hover:bg-amber-500/20',
  };
  return (
    <button
      type='button'
      title={title}
      disabled={disabled}
      onClick={onClick}
      className={`p-2 rounded-lg border transition-colors disabled:opacity-40 ${cls[variant]}`}
    >
      {icon}
    </button>
  );
}

// ── Logs modal ───────────────────────────────────────────────────────────────

function LogsModal(
  { name, scope, onClose }: { name: string; scope: EffectiveScope; onClose: () => void },
) {
  const [tail, setTail] = useState(300);
  const [timestamps, setTimestamps] = useState(false);
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['docker-logs', scope.node, name, tail, timestamps],
    queryFn: () =>
      callWidgetTool<{ ok?: boolean; logs?: string; error?: string }>(
        T.logs,
        { container: name, tail, timestamps },
        scope,
        { silent: true },
      ),
    refetchInterval: 5_000,
    retry: 1,
  });

  let body: string;
  if (q.isLoading && !q.data) body = t('containers.loadingLogs', 'Načítám logy…');
  else if (q.isError) {
    body = t('containers.errorPrefix', 'Chyba: {{msg}}', {
      msg: (q.error as Error)?.message ?? t('containers.loadFailed', 'načtení selhalo'),
    });
  }
  else if (q.data?.ok === false) body = q.data.error ?? t('containers.toolError', 'Tool vrátil chybu');
  else if (q.data?.logs != null && q.data.logs !== '') body = q.data.logs;
  else if (q.data?.logs === '') body = t('containers.emptyLogs', '(prázdné logy)');
  else body = t('containers.empty', '(prázdné)');

  return (
    <Modal open onClose={onClose} title={t('containers.logsTitle', 'Logy · {{name}}', { name })} size='xl'>
      <div className='flex items-center gap-2 mb-3 flex-wrap'>
        <span className='text-xs text-gray-500'>Tail</span>
        {([100, 300, 1000] as const).map((n) => (
          <button
            key={n}
            type='button'
            onClick={() => setTail(n)}
            className={`px-2 py-1 rounded-lg text-xs border ${
              tail === n
                ? 'bg-blue-600 border-blue-500 text-white'
                : 'border-gray-700 text-gray-400 hover:text-white'
            }`}
          >
            {n}
          </button>
        ))}
        <label className='ml-2 inline-flex items-center gap-1.5 text-xs text-gray-400 cursor-pointer'>
          <input
            type='checkbox'
            checked={timestamps}
            onChange={(e) => setTimestamps(e.target.checked)}
            className='rounded border-gray-600'
          />
          timestamps
        </label>
        <div className='ml-auto flex gap-1.5'>
          <Button
            size='sm'
            variant='secondary'
            icon={<Copy size={13} />}
            onClick={async () => {
              if (await copyText(body)) toast.success(t('containers.logsCopied', 'Logy zkopírovány'));
              else toast.error(t('common.copyFailed', 'Clipboard selhal'));
            }}
          >
            {t('common.copy', 'Kopírovat')}
          </Button>
          <Button
            size='sm'
            variant='secondary'
            icon={<Download size={13} />}
            onClick={() => downloadText(`${name}-logs.txt`, body, 'text/plain')}
          >
            {t('containers.download', 'Stáhnout')}
          </Button>
          <RefreshButton onRefresh={() => q.refetch()} loading={q.isFetching} size='sm' />
        </div>
      </div>
      <pre className='acm-terminal text-sm whitespace-pre-wrap leading-relaxed max-h-[60vh] overflow-auto rounded-xl p-4'>
        {body}
      </pre>
    </Modal>
  );
}

// ── Inspect / Config modal ───────────────────────────────────────────────────

type InspectTab = 'overview' | 'config' | 'export';

function InspectModal(
  { name, scope, onClose }: { name: string; scope: EffectiveScope; onClose: () => void },
) {
  const [tab, setTab] = useState<InspectTab>('overview');
  const { t } = useTranslation();
  const q = useQuery({
    queryKey: ['docker-inspect', scope.node, name],
    queryFn: () =>
      callWidgetTool<{
        ok?: boolean;
        error?: string;
        image?: string;
        status?: string;
        restart?: string;
        ports?: string[];
        volumes?: string[];
        env?: string[];
        labels?: Record<string, string>;
        acm_labels?: Record<string, string>;
        networks?: string[];
        cmd?: string[];
        entrypoint?: string[];
        workdir?: string;
        user?: string;
        created?: string;
        module?: string;
        deployment?: string;
        instance?: string;
        service?: string;
        export_config?: Record<string, unknown>;
      }>(T.inspect, { container: name }, scope, { silent: true }),
  });
  const d = q.data;
  const exportJson = d?.export_config
    ? JSON.stringify(d.export_config, null, 2)
    : d
    ? JSON.stringify(d, null, 2)
    : '';

  return (
    <Modal open onClose={onClose} title={t('containers.configTitle', 'Konfigurace · {{name}}', { name })} size='lg'>
      {q.isLoading
        ? <p className='text-sm text-gray-500'>{t('common.loading', 'Načítám…')}</p>
        : q.isError
        ? <p className='text-sm text-red-400'>{(q.error as Error)?.message}</p>
        : d?.ok === false
        ? <p className='text-sm text-red-400'>{d.error}</p>
        : (
          <div className='space-y-4 text-sm'>
            <div className='flex items-center gap-1 flex-wrap border-b border-gray-800 pb-2'>
              {([
                ['overview', t('containers.tabOverview', 'Přehled')],
                ['config', 'Config'],
                ['export', 'Export'],
              ] as const).map(([id, label]) => (
                <button
                  key={id}
                  type='button'
                  onClick={() => setTab(id)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${
                    tab === id
                      ? 'bg-blue-600 text-white'
                      : 'text-gray-400 hover:text-white hover:bg-gray-800'
                  }`}
                >
                  {label}
                </button>
              ))}
              <div className='ml-auto flex gap-1.5'>
                <Button
                  size='sm'
                  variant='secondary'
                  icon={<Copy size={13} />}
                  onClick={async () => {
                    if (await copyText(exportJson)) toast.success(t('containers.jsonCopied', 'JSON zkopírován'));
                    else toast.error(t('common.copyFailed', 'Clipboard selhal'));
                  }}
                >
                  {t('common.copy', 'Kopírovat')}
                </Button>
                <Button
                  size='sm'
                  variant='secondary'
                  icon={<Download size={13} />}
                  onClick={() => downloadText(`${name}-config.json`, exportJson)}
                >
                  {t('containers.downloadJson', 'Stáhnout JSON')}
                </Button>
              </div>
            </div>

            {tab === 'overview' && (
              <div className='space-y-2'>
                {[
                  ['Image', d?.image],
                  ['Status', d?.status],
                  ['Restart', d?.restart],
                  [t('containers.moduleLabel', 'Modul'), d?.module],
                  [t('containers.projectLabel', 'Projekt'), d?.deployment],
                  ['Instance', d?.instance],
                  ['Service', d?.service],
                  ['Created', d?.created],
                  ['User', d?.user],
                  ['Workdir', d?.workdir],
                  ['Networks', (d?.networks ?? []).join(', ')],
                  ['Ports', (d?.ports ?? []).join(', ')],
                  ['Cmd', (d?.cmd ?? []).join(' ')],
                  ['Entrypoint', (d?.entrypoint ?? []).join(' ')],
                ].map(([k, v]) =>
                  v
                    ? (
                      <div key={String(k)} className='flex gap-4 border-b border-gray-800/60 pb-2'>
                        <span className='w-28 shrink-0 text-xs font-semibold text-gray-500 uppercase tracking-wide'>
                          {k}
                        </span>
                        <span className='text-gray-200 font-mono break-all'>{v}</span>
                      </div>
                    )
                    : null
                )}
              </div>
            )}

            {tab === 'config' && (
              <div className='space-y-4'>
                {(d?.volumes?.length ?? 0) > 0 && (
                  <div>
                    <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>
                      Volumes
                    </p>
                    <pre className='acm-terminal text-xs whitespace-pre-wrap rounded-xl p-3'>
                      {d!.volumes!.join('\n')}
                    </pre>
                  </div>
                )}
                {(d?.env?.length ?? 0) > 0 && (
                  <div>
                    <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>
                      {t('containers.envMasked', 'Env (secrets maskované)')}
                    </p>
                    <pre className='acm-terminal text-xs whitespace-pre-wrap max-h-48 overflow-auto rounded-xl p-3'>
                      {d!.env!.join('\n')}
                    </pre>
                  </div>
                )}
                {Object.keys(d?.acm_labels ?? d?.labels ?? {}).length > 0 && (
                  <div>
                    <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>
                      {t('containers.labelsHeading', 'Labely')}
                    </p>
                    <pre className='acm-terminal text-xs whitespace-pre-wrap max-h-48 overflow-auto rounded-xl p-3'>
                      {Object.entries(d?.acm_labels ?? {})
                        .map(([k, v]) => `${k}=${v}`)
                        .join('\n')
                        || Object.entries(d?.labels ?? {})
                          .filter(([k]) => k.startsWith('acm.'))
                          .map(([k, v]) => `${k}=${v}`)
                          .join('\n')
                        || '—'}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {tab === 'export' && (
              <div>
                <p className='text-xs text-gray-500 mb-2'>
                  {t('containers.exportHint', 'Exportovatelná konfigurace (env s maskovanými secrets) — pro backup / recreate.')}
                </p>
                <pre className='acm-terminal text-xs whitespace-pre-wrap max-h-[50vh] overflow-auto rounded-xl p-3'>
                  {exportJson}
                </pre>
              </div>
            )}
          </div>
        )}
    </Modal>
  );
}

// ── Deploy / create modal ────────────────────────────────────────────────────

function DeployModal(
  { scope, onClose, onDone }: { scope: EffectiveScope; onClose: () => void; onDone: () => void },
) {
  const [name, setName] = useState('');
  const [image, setImage] = useState('');
  const [module, setModule] = useState('custom');
  const [project, setProject] = useState('');
  const [service, setService] = useState('default');
  const [ports, setPorts] = useState('');
  const { t } = useTranslation();
  const create = useMutation({
    mutationFn: () =>
      callWidgetTool(T.create, {
        name: name.trim(),
        image: image.trim(),
        module: module.trim() || 'custom',
        deployment: project.trim() || undefined,
        service: service.trim() || 'default',
        ports: ports.split('\n').map((s) => s.trim()).filter(Boolean),
        restart: 'unless-stopped',
      }, scope),
    onSuccess: (r) => {
      if ((r as { ok?: boolean })?.ok === false) {
        toast.error(String((r as { error?: string }).error ?? t('containers.createFailed', 'Create selhal')));
        return;
      }
      toast.success(t('containers.createdToast', 'Vytvořeno: {{name}}', { name }));
      onDone();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  return (
    <Modal open onClose={onClose} title={t('containers.deployContainerTitle', 'Deploy kontejner')} size='md'>
      <div className='space-y-4'>
        <label className='block text-xs font-semibold text-gray-400'>
          {t('containers.nameLabel', 'Název')}
          <Input className='mt-1.5 font-mono' value={name} onChange={(e) => setName(e.target.value)} />
        </label>
        <label className='block text-xs font-semibold text-gray-400'>
          Image
          <Input
            className='mt-1.5 font-mono'
            value={image}
            onChange={(e) => setImage(e.target.value)}
            placeholder='nginx:alpine'
          />
        </label>
        <div className='grid grid-cols-1 sm:grid-cols-3 gap-3'>
          <label className='block text-xs font-semibold text-gray-400'>
            {t('containers.acmModule', 'ACM modul')}
            <Input
              className='mt-1.5 font-mono'
              value={module}
              onChange={(e) => setModule(e.target.value)}
            />
          </label>
          <label className='block text-xs font-semibold text-gray-400'>
            {t('containers.projectLabel', 'Projekt')}
            <Input
              className='mt-1.5 font-mono'
              value={project}
              onChange={(e) => setProject(e.target.value)}
              placeholder={t('containers.optional', 'volitelné')}
            />
          </label>
          <label className='block text-xs font-semibold text-gray-400'>
            Service
            <Input
              className='mt-1.5 font-mono'
              value={service}
              onChange={(e) => setService(e.target.value)}
              placeholder='default'
            />
          </label>
        </div>
        <label className='block text-xs font-semibold text-gray-400'>
          {t('containers.portsOnePerLine', 'Porty (host:container, jeden na řádek)')}
          <textarea
            className='mt-1.5 w-full rounded-xl border border-gray-800 bg-gray-950/60 px-4 py-2 text-sm text-white font-mono placeholder:text-gray-500 focus:outline-none focus:border-blue-500/40'
            rows={3}
            value={ports}
            onChange={(e) => setPorts(e.target.value)}
            placeholder={'8080:80\n'}
          />
        </label>
        <div className='flex justify-end gap-2 pt-1'>
          <Button size='md' variant='ghost' onClick={onClose}>{t('common.cancel', 'Zrušit')}</Button>
          <Button
            size='md'
            variant='primary'
            loading={create.isPending}
            disabled={!name.trim() || !image.trim()}
            onClick={() => create.mutate()}
            icon={<Plus size={14} />}
          >
            Deploy
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// ── Production lock ──────────────────────────────────────────────────────────

interface LockInfo {
  resource_id: string;
  locked_by: string;
  locked_at?: string;
  reason?: string | null;
  service_id?: string;
}

function useLocks(scope: EffectiveScope) {
  return useQuery({
    // Do not share a cache key with ProductionLockPanel, whose query returns
    // {locks:[...]} instead of an array. The shape collision crashed the
    // whole Mesh/Infrastructure page with "is not iterable".
    queryKey: ['registry-docker-production-locks-list', scope.node ?? 'local'],
    queryFn: async () => {
      try {
        const r = await callWidgetTool<{ ok?: boolean; locks?: LockInfo[] }>(
          T.lock,
          { action: 'list' },
          scope,
          { silent: true },
        );
        return r.locks ?? [];
      } catch {
        return [];
      }
    },
    staleTime: 30_000,
    retry: false,
  });
}

function LockRow({
  containerId,
  lock,
  onChanged,
}: {
  containerId: string;
  lock: LockInfo | null;
  onChanged: () => void;
}) {
  const [reason, setReason] = useState('');
  const [pin, setPin] = useState('');
  const [showUnlock, setShowUnlock] = useState(false);
  const [revealedPin, setRevealedPin] = useState<string | null>(null);
  const { t } = useTranslation();

  // PIN je skrytý; odkrytí je session-gated a auditované (agent dostane 403).
  const revealMut = useMutation({
    mutationFn: () =>
      mcpCall<{ ok: boolean; pin?: string; error?: string }>(T.lock, {
        action: 'reveal',
        resource_id: containerId,
      }),
    onSuccess: (res) => {
      if (res.ok && res.pin) setRevealedPin(res.pin);
      else toast.error(res.error ?? t('containers.pinRevealFailed', 'Odkrytí PINu selhalo'));
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const lockMut = useMutation({
    mutationFn: () =>
      mcpCall<{ ok?: boolean }>(T.lock, {
        action: 'set',
        resource_id: containerId,
        reason: reason || t('containers.defaultLockReason', 'produkční služba'),
      }),
    onSuccess: () => {
      toast.success(
        t('containers.lockedToast', '🔒 Zamčeno. PIN odkryješ u kontejneru.'),
        { duration: 15_000 },
      );
      setReason('');
      onChanged();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const unlockMut = useMutation({
    mutationFn: () =>
      mcpCall<{ ok: boolean; error?: string }>(T.lock, {
        action: 'remove',
        resource_id: containerId,
        pin,
      }),
    onSuccess: (r) => {
      if (r.ok) {
        toast.success(t('containers.unlockedToast', '🔓 Odemčeno'));
        setPin('');
        setShowUnlock(false);
        onChanged();
      } else {
        toast.error(r.error ?? t('containers.unlockFailed', 'Odemčení selhalo'));
      }
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (lock) {
    return (
      <div className='flex items-center gap-2 min-h-8 px-1.5 py-1 rounded-lg bg-amber-500/5 border border-amber-500/15'>
        <LockIcon size={13} className='text-amber-400 shrink-0' />
        <span className='text-xs text-white font-mono truncate min-w-0 flex-1' title={containerId}>
          {containerId}
        </span>
        {lock.service_id && <Badge variant='warning' className='text-[9px] shrink-0'>{lock.service_id}</Badge>}
        <button
          type='button'
          className='text-[10px] font-mono text-amber-300 shrink-0 hover:text-amber-200'
          title={t('containers.pinHiddenHint', 'PIN je skrytý — odkrytí je auditované a jen pro přihlášeného člověka')}
          onClick={() => (revealedPin ? setRevealedPin(null) : revealMut.mutate())}
        >
          {revealedPin ? `PIN ${revealedPin}` : 'PIN ••••••'}
        </button>
        {showUnlock
          ? (
            <div className='flex items-center gap-1 shrink-0'>
              <Input
                size='sm'
                className='w-16 font-mono h-7'
                placeholder='PIN'
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && unlockMut.mutate()}
                autoFocus
              />
              <Button
                variant='danger'
                size='icon'
                loading={unlockMut.isPending}
                onClick={() => unlockMut.mutate()}
                title={t('containers.unlock', 'Odemknout')}
                aria-label={t('containers.unlock', 'Odemknout')}
                icon={<Unlock size={13} />}
              />
              <Button variant='ghost' size='icon' onClick={() => setShowUnlock(false)} title={t('common.cancel', 'Zrušit')}>
                ×
              </Button>
            </div>
          )
          : (
            <Button
              variant='ghost'
              size='icon'
              className='shrink-0 text-amber-400/90'
              onClick={() => setShowUnlock(true)}
              title={t('containers.unlock', 'Odemknout')}
              aria-label={t('containers.unlock', 'Odemknout')}
              icon={<Unlock size={13} />}
            />
          )}
      </div>
    );
  }

  return (
    <div className='flex items-center gap-2 min-h-8 px-1.5 py-1 rounded-lg border border-gray-800/50'>
      <Unlock size={13} className='text-gray-600 shrink-0' />
      <span className='text-xs text-gray-400 font-mono truncate min-w-0 flex-1' title={containerId}>
        {containerId}
      </span>
      {showUnlock
        ? (
          <div className='flex items-center gap-1 shrink-0'>
            <Input
              size='sm'
              className='w-28 h-7'
              placeholder={t('containers.reasonPlaceholder', 'Důvod (volitelný)')}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              onKeyDown={(e) => e.key === 'Enter' && lockMut.mutate()}
              autoFocus
            />
            <Button
              variant='warning'
              size='icon'
              loading={lockMut.isPending}
              onClick={() => lockMut.mutate()}
              title={t('containers.lock', 'Zamknout')}
              aria-label={t('containers.lock', 'Zamknout')}
              icon={<LockIcon size={13} />}
            />
            <Button
              variant='ghost'
              size='icon'
              onClick={() => {
                setShowUnlock(false);
                setReason('');
              }}
              title={t('common.cancel', 'Zrušit')}
            >
              ×
            </Button>
          </div>
        )
        : (
          <Button
            variant='ghost'
            size='icon'
            className='shrink-0 text-gray-500 hover:text-amber-300'
            onClick={() => setShowUnlock(true)}
            title={t('containers.lock', 'Zamknout')}
            aria-label={t('containers.lock', 'Zamknout')}
            icon={<LockIcon size={13} />}
          />
        )}
    </div>
  );
}

/** Hromadný zámek odemčených kontejnerů ve skupině — jeden důvod, N MCP callů. */
function GroupLockAll({
  names,
  onDone,
}: {
  names: string[];
  onDone: () => void;
}) {
  const [reason, setReason] = useState('');
  const { t } = useTranslation();
  const mut = useMutation({
    mutationFn: async () => {
      const r = reason.trim() || t('containers.lockGroupReason', 'produkční skupina');
      const results: Array<{ name: string; ok: boolean; error?: string }> = [];
      for (const name of names) {
        try {
          await mcpCall<{ ok?: boolean; error?: string }>(
            T.lock,
            { action: 'set', resource_id: name, reason: r },
          );
          results.push({ name, ok: true });
        } catch (e) {
          results.push({ name, ok: false, error: (e as Error).message });
        }
      }
      return results;
    },
    onSuccess: (results) => {
      const ok = results.filter((r) => r.ok);
      const fail = results.filter((r) => !r.ok);
      if (ok.length) {
        toast.success(
          t('containers.lockedCountToast', '🔒 Zamčeno {{ok}}/{{total}}. PINy odkryješ u kontejnerů.', {
            ok: ok.length,
            total: names.length,
          }),
          { duration: 20_000 },
        );
      }
      if (fail.length) {
        toast.error(t('containers.lockFailedNames', 'Selhalo: {{names}}', {
          names: fail.map((f) => f.name).join(', '),
        }));
      }
      setReason('');
      onDone();
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (names.length === 0) {
    return (
      <p className='text-sm text-emerald-400/80 px-1 py-1'>{t('containers.allLockedInGroup', 'Všechny kontejnery ve skupině jsou zamčené.')}</p>
    );
  }

  return (
    <div className='flex items-center gap-3 px-4 py-3 rounded-xl border border-amber-500/25 bg-amber-500/5 flex-wrap'>
      <LockIcon size={16} className='text-amber-400 shrink-0' />
      <div className='flex-1 min-w-0'>
        <p className='text-sm text-white font-medium'>{t('containers.lockWholeGroup', 'Zamknout celou skupinu')}</p>
        <p className='text-xs text-gray-500'>{t('containers.unlockedCount', '{{count}} odemčených kontejnerů', { count: names.length })}</p>
      </div>
      <Input
        size='sm'
        className='w-52'
        placeholder={t('containers.reasonPlaceholder', 'Důvod (volitelný)')}
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <Button
        variant='warning'
        size='sm'
        loading={mut.isPending}
        onClick={() => mut.mutate()}
        icon={<LockIcon size={14} />}
      >
        {t('containers.lockAll', 'Zamknout vše')}
      </Button>
    </div>
  );
}

// ── Container list ───────────────────────────────────────────────────────────

export function ContainerList({
  scope,
  moduleFilter,
  projectFilter,
  prefixFilter,
  controls,
  compact = false,
  hideWhenEmpty = false,
  title,
  className,
}: ContainerListProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  // Embed pod modulem = compact default; plná stránka = controls default true.
  const scoped = !!(moduleFilter || projectFilter || prefixFilter);
  const showControls = controls ?? (!compact && !scoped);
  const isCompact = compact || (scoped && !showControls);

  const listArgs = useMemo(() => {
    const a: Record<string, unknown> = { all: true };
    if (moduleFilter) a.module = moduleFilter;
    if (projectFilter) a.deployment = projectFilter;
    return a;
  }, [moduleFilter, projectFilter]);

  const q = useWidgetData<ListResp>(T.list, listArgs, scope, {
    refetchInterval: 12_000,
  });
  const locksQ = useLocks(scope);
  const lockMap = useMemo(() => {
    const m = new Map<string, LockInfo>();
    for (const l of locksQ.data ?? []) {
      if (l.resource_id) m.set(l.resource_id, l);
    }
    return m;
  }, [locksQ.data]);
  const lockSet = useMemo(() => new Set(lockMap.keys()), [lockMap]);

  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState<FilterMode>('all');
  // Default: pod modulem → projekty; globálně → podle modulu (s projekty uvnitř).
  // Přepínač: Modul | Projekt (orch) | Flat.
  const [groupMode, setGroupMode] = useState<GroupMode>(
    moduleFilter || projectFilter ? 'project' : 'module',
  );
  const [sectionOpen, setSectionOpen] = useState(true);
  const [expanded, setExpanded] = useState<string | null>(null);
  /** Sbalené skupiny (default = otevřené). */
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => new Set());
  /** Která skupina má rozbalený panel zámků. */
  const [groupLockOpen, setGroupLockOpen] = useState<string | null>(null);
  const [pending, setPending] = useState<{ name: string; action: CAction } | null>(null);
  const [logs, setLogs] = useState<string | null>(null);
  const [inspect, setInspect] = useState<string | null>(null);
  const [deploy, setDeploy] = useState(false);

  const act = useMutation({
    mutationFn: ({ container, action }: { container: string; action: CAction }) =>
      callWidgetTool(T.action, {
        container,
        action,
        force: action === 'remove' ? true : undefined,
      }, scope),
    onMutate: (v) => setPending({ name: v.container, action: v.action }),
    onSuccess: (_r, v) => {
      if ((_r as { ok?: boolean })?.ok === false) {
        toast.error(String((_r as { error?: string }).error ?? t('containers.actionFailed', 'Akce selhala')));
      } else {
        toast.success(`${v.action} → ${v.container}`);
      }
      q.refetch();
      locksQ.refetch();
    },
    onError: (e) => toast.error((e as Error).message || t('containers.actionFailed', 'Akce selhala')),
    onSettled: () => setPending(null),
  });

  const onAction = async (name: string, action: CAction) => {
    if (lockSet.has(name) && (action === 'stop' || action === 'restart' || action === 'remove')) {
      toast.error(t('containers.lockedNeedUnlock', 'Kontejner je zamčený (production lock). Odemkni nejdřív.'));
      return;
    }
    if (action === 'remove' && !(await themedConfirm(t('containers.confirmDelete', 'Smazat kontejner „{{name}}"?', { name })))) return;
    act.mutate({ container: name, action });
  };

  /** Skupinová akce (start/stop/restart) — jako stará DeploymentGroup. */
  const groupAction = (names: string[], action: 'start' | 'stop' | 'restart') => {
    const targets = names
      .map(clean)
      .filter((n) => {
        if (lockSet.has(n) && (action === 'stop' || action === 'restart')) {
          return false;
        }
        return true;
      });
    const skipped = names.length - targets.length;
    if (skipped > 0) {
      toast.message(t('containers.lockedSkipped', '{{count}} zamčených přeskočeno (odemkni nejdřív)', { count: skipped }));
    }
    if (targets.length === 0) {
      toast.error(t('containers.noTargets', 'Žádný kontejner k akci (vše zamčeno?).'));
      return;
    }
    for (const n of targets) act.mutate({ container: n, action });
  };

  const toggleGroupCollapsed = (key: string) => {
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const all = q.data?.containers ?? [];
  const list = useMemo(() => {
    const s = search.trim().toLowerCase();
    return all.filter((c) => {
      const n = clean(c.name);
      // Server už filtruje module/deployment; client-side fallback pro prefix + jistotu.
      if (moduleFilter && !belongsToModule(c, moduleFilter)) return false;
      if (projectFilter && c.deployment !== projectFilter) return false;
      if (prefixFilter && !matchesModuleName(c.name, prefixFilter)) return false;
      if (filter === 'running' && c.state !== 'running') return false;
      if (filter === 'stopped' && c.state === 'running') return false;
      if (
        s &&
        !(
          n.toLowerCase().includes(s) ||
          c.image?.toLowerCase().includes(s) ||
          c.module?.toLowerCase().includes(s) ||
          c.deployment?.toLowerCase().includes(s) ||
          c.instance?.toLowerCase().includes(s) ||
          c.service?.toLowerCase().includes(s)
        )
      ) return false;
      return true;
    });
  }, [all, search, filter, moduleFilter, projectFilter, prefixFilter]);

  const running = list.filter((c) => c.state === 'running').length;
  const stopped = list.length - running;

  /**
   * groupMode=module  → modul → projekt (deployment/instance)
   * groupMode=project → projekt (orch) nahoře; pod ním kontejnery (případně víc modulů)
   * groupMode=flat    → null
   */
  const groups = useMemo((): ModuleGroup[] | null => {
    if (groupMode === 'flat') return null;

    if (groupMode === 'project') {
      // Invertovaná hierarchie: „moduleKey“ = project, „projects“ = jeden bucket s rows
      // (nebo split podle modulu jako sub-projekty).
      const projMap = new Map<string, Map<string, ContainerRow[]>>();
      for (const c of list) {
        const pKey = resolveProjectKey(c);
        let modKey = c.module?.trim() || '';
        if (!modKey && moduleFilter && belongsToModule(c, moduleFilter)) modKey = moduleFilter;
        if (!modKey) modKey = '_other';
        if (!projMap.has(pKey)) projMap.set(pKey, new Map());
        const mMap = projMap.get(pKey)!;
        if (!mMap.has(modKey)) mMap.set(modKey, []);
        mMap.get(modKey)!.push(c);
      }
      const result: ModuleGroup[] = [];
      for (const [projectKey, mMap] of projMap) {
        const projects: ProjectGroup[] = [];
        for (const [moduleKey, rows] of mMap) {
          projects.push({
            projectKey: moduleKey,
            projectLabel: moduleKey === '_other' ? t('containers.other', 'ostatní') : moduleKey,
            rows,
          });
        }
        projects.sort((a, b) => b.rows.length - a.rows.length || a.projectLabel.localeCompare(b.projectLabel));
        const flat = projects.flatMap((p) => p.rows);
        result.push({
          moduleKey: projectKey,
          moduleLabel: projectGroupLabel(projectKey, flat),
          projects,
          total: flat.length,
          running: flat.filter((x) => x.state === 'running').length,
        });
      }
      return result.sort((a, b) => b.running - a.running || a.moduleLabel.localeCompare(b.moduleLabel));
    }

    // groupMode === 'module'
    const modMap = new Map<string, Map<string, ContainerRow[]>>();
    for (const c of list) {
      let modKey = c.module?.trim() || '';
      if (!modKey && moduleFilter && belongsToModule(c, moduleFilter)) {
        modKey = moduleFilter;
      }
      if (!modKey) modKey = '_other';
      const projKey = resolveProjectKey(c);
      if (!modMap.has(modKey)) modMap.set(modKey, new Map());
      const pMap = modMap.get(modKey)!;
      if (!pMap.has(projKey)) pMap.set(projKey, []);
      pMap.get(projKey)!.push(c);
    }

    const result: ModuleGroup[] = [];
    for (const [moduleKey, pMap] of modMap) {
      const projects: ProjectGroup[] = [];
      for (const [projectKey, rows] of pMap) {
        projects.push({
          projectKey,
          projectLabel: projectKey === '_other'
            ? t('containers.other', 'ostatní')
            : projectGroupLabel(projectKey, rows),
          rows,
        });
      }
      projects.sort((a, b) => {
        const ra = a.rows.filter((x) => x.state === 'running').length;
        const rb = b.rows.filter((x) => x.state === 'running').length;
        return rb - ra || a.projectLabel.localeCompare(b.projectLabel);
      });
      const flat = projects.flatMap((p) => p.rows);
      result.push({
        moduleKey,
        moduleLabel: moduleKey === '_other' ? t('containers.unclassified', 'Neklasifikované') : moduleKey,
        projects,
        total: flat.length,
        running: flat.filter((x) => x.state === 'running').length,
      });
    }
    return result.sort((a, b) => b.running - a.running || a.moduleLabel.localeCompare(b.moduleLabel));
  }, [list, groupMode, moduleFilter, t]);

  const moduleCount = useMemo(() => {
    return new Set(list.map((c) => c.module).filter(Boolean)).size;
  }, [list]);
  const projectCount = useMemo(() => {
    return new Set(list.map((c) => resolveProjectKey(c)).filter((k) => k !== '_other')).size;
  }, [list]);

  const busy = (name: string) => pending?.name === name;

  const renderRow = (c: ContainerRow) => {
    const cname = clean(c.name);
    const isRunning = c.state === 'running';
    const isOpen = expanded === cname;
    const locked = lockSet.has(cname);
    const isBusy = busy(cname);
    const proj = resolveProjectKey(c);
    const displayName = c.service || cname;

    return (
      <div key={cname} className='border-b border-gray-800/50 last:border-0'>
        <div
          className='flex items-center gap-3 px-4 py-3 hover:bg-gray-800/25 transition-colors cursor-pointer'
          onClick={() => setExpanded(isOpen ? null : cname)}
        >
          {isBusy
            ? <Loader2 size={14} className='text-blue-400 animate-spin flex-shrink-0' />
            : <StatusDot status={c.state} className='flex-shrink-0' />}

          <div className='w-48 shrink-0 min-w-0'>
            <div className='text-sm font-mono text-white truncate' title={cname}>
              {locked && <LockIcon size={12} className='text-amber-400 inline mr-1.5 -mt-0.5' />}
              {displayName}
            </div>
            <div className='flex items-center gap-1.5 mt-0.5 min-w-0'>
              {c.service && (
                <span className='text-[10px] text-gray-600 font-mono truncate' title={cname}>
                  {cname}
                </span>
              )}
              {groupMode !== 'project' && proj !== '_other' && (
                <span className='text-[10px] text-violet-400/80 font-mono truncate' title={t('containers.projectColon', 'projekt: {{id}}', { id: proj })}>
                  · {proj}
                </span>
              )}
            </div>
          </div>

          <div className='flex-1 min-w-0 text-xs text-gray-500 font-mono truncate' title={c.image}>
            {c.image}
          </div>

          <div className='w-24 shrink-0 hidden md:block'>
            <StatusDot status={c.state} withLabel label={c.state} />
          </div>

          <div className='w-16 shrink-0 hidden lg:flex items-center gap-1 text-xs text-gray-400 font-mono'>
            <Cpu size={12} className='text-gray-600' />
            {c.stats?.cpu ?? '—'}
          </div>
          <div className='w-16 shrink-0 hidden lg:flex items-center gap-1 text-xs text-gray-400 font-mono'>
            <MemoryStick size={12} className='text-gray-600' />
            {c.stats?.mem_perc ?? '—'}
          </div>
          <div className='w-28 shrink-0 hidden xl:block text-xs text-gray-600 font-mono truncate'>
            {c.ports || '—'}
          </div>

          <div
            className='flex items-center gap-1 shrink-0'
            onClick={(e) => e.stopPropagation()}
          >
            {!isRunning
              ? (
                <IconBtn
                  icon={<Play size={13} />}
                  title='Start'
                  variant='success'
                  disabled={isBusy}
                  onClick={() => onAction(cname, 'start')}
                />
              )
              : (
                <IconBtn
                  icon={<Square size={13} />}
                  title='Stop'
                  variant='warning'
                  disabled={isBusy}
                  onClick={() => onAction(cname, 'stop')}
                />
              )}
            <IconBtn
              icon={<RotateCw size={13} />}
              title='Restart'
              variant='muted'
              disabled={isBusy}
              onClick={() => onAction(cname, 'restart')}
            />
            <IconBtn
              icon={<FileJson size={13} />}
              title={t('containers.configExport', 'Konfigurace / export')}
              variant='info'
              onClick={() => setInspect(cname)}
            />
            <IconBtn
              icon={<ScrollText size={13} />}
              title={t('containers.actionLogs', 'Logy')}
              variant='muted'
              onClick={() => setLogs(cname)}
            />
            <IconBtn
              icon={<LockIcon size={13} />}
              title={locked ? t('containers.lockedExpandPin', 'Zamčeno — rozbal pro PIN') : t('containers.lockNoun', 'Zámek')}
              variant={locked ? 'amber' : 'muted'}
              onClick={() => setExpanded(isOpen ? null : cname)}
            />
            <IconBtn
              icon={<Trash2 size={13} />}
              title={t('common.delete', 'Smazat')}
              variant='danger'
              disabled={isBusy}
              onClick={() => onAction(cname, 'remove')}
            />
          </div>
        </div>

        {isOpen && (
          <div className='px-4 pb-4 pt-3 bg-gray-900/40 border-t border-gray-800/50 space-y-3'>
            <div className='flex flex-wrap gap-2'>
              <Button size='sm' variant='secondary' icon={<ScrollText size={13} />} onClick={() => setLogs(cname)}>
                {t('containers.actionLogs', 'Logy')}
              </Button>
              <Button size='sm' variant='secondary' icon={<FileJson size={13} />} onClick={() => setInspect(cname)}>
                {t('containers.config', 'Konfigurace')}
              </Button>
            </div>
            <div className='grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-3 text-sm'>
              <div>
                <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>Image</p>
                <p className='text-gray-300 font-mono break-all'>{c.image}</p>
              </div>
              {c.ports && (
                <div>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>{t('containers.portsLabel', 'Porty')}</p>
                  <p className='text-gray-300 font-mono'>{c.ports}</p>
                </div>
              )}
              {c.module && (
                <div>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>{t('containers.moduleLabel', 'Modul')}</p>
                  <p className='text-gray-300 font-mono'>{c.module}</p>
                </div>
              )}
              <div>
                <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>{t('containers.projectLabel', 'Projekt')}</p>
                <p className='text-gray-300 font-mono'>
                  {c.deployment || (proj !== '_other' ? proj : '—')}
                </p>
              </div>
              {c.instance && (
                <div>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>Instance</p>
                  <p className='text-gray-300 font-mono'>{c.instance}</p>
                </div>
              )}
              {c.service && (
                <div>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>Service</p>
                  <p className='text-gray-300 font-mono'>{c.service}</p>
                </div>
              )}
              {c.id && (
                <div>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>ID</p>
                  <p className='text-gray-400 font-mono'>{c.id.slice(0, 12)}</p>
                </div>
              )}
              {c.created && (
                <div>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>{t('containers.created', 'Vytvořen')}</p>
                  <p className='text-gray-300'>{c.created}</p>
                </div>
              )}
              {c.stats && (
                <div>
                  <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>Resources</p>
                  <p className='text-gray-300 font-mono'>
                    CPU {c.stats.cpu} · RAM {c.stats.mem} ({c.stats.mem_perc})
                  </p>
                </div>
              )}
              <div>
                <p className='text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1'>Status</p>
                <p className='text-gray-300'>{c.status}</p>
              </div>
            </div>
            <LockRow
              containerId={cname}
              lock={lockMap.get(cname) ?? null}
              onChanged={() => locksQ.refetch()}
            />
          </div>
        )}
      </div>
    );
  };

  const tableHead = (
    <div className='hidden md:flex items-center gap-3 px-4 py-2 text-[11px] font-semibold uppercase tracking-wider text-gray-600 border-b border-gray-800 bg-gray-900/50 sticky top-0 z-10'>
      <span className='w-2 shrink-0' />
      <span className='w-44 shrink-0'>{t('containers.nameLabel', 'Název')}</span>
      <span className='flex-1'>Image</span>
      <span className='w-24 shrink-0'>{t('containers.stateLabel', 'Stav')}</span>
      <span className='w-16 shrink-0 hidden lg:block'>CPU</span>
      <span className='w-16 shrink-0 hidden lg:block'>RAM</span>
      <span className='w-28 shrink-0 hidden xl:block'>{t('containers.portsLabel', 'Porty')}</span>
      <span className='w-[13.5rem] shrink-0 text-right'>{t('containers.actions', 'Akce')}</span>
    </div>
  );

  // Empty scoped embed → nic nerenderovat (modul bez kontejnerů nemá patičku).
  // Během loadu taky ne — ať nebliká prázdná hlavička na modulech bez containers.
  if (
    hideWhenEmpty &&
    (q.isLoading || (list.length === 0 && !search && filter === 'all'))
  ) {
    return null;
  }

  const sectionTitle = title
    ?? (moduleFilter
      ? t('containers.moduleTitle', 'Kontejnery · {{module}}', { module: moduleFilter })
      : t('containers.pageTitle', 'Kontejnery'));

  const groupModeBar = (
    <div className='flex rounded-xl border border-gray-800 overflow-hidden'>
      {([
        ['module', t('containers.groupModules', 'Moduly')],
        ['project', t('containers.groupProjects', 'Projekty')],
        ['flat', t('containers.groupFlat', 'Flat')],
      ] as const).map(([id, label]) => (
        <button
          key={id}
          type='button'
          title={
            id === 'module'
              ? t('containers.groupByModule', 'Seskupit: ACM modul → projekt')
              : id === 'project'
              ? t('containers.groupByProject', 'Seskupit: projekt (orch acm.deployment / instance)')
              : t('containers.flatList', 'Plochý seznam')
          }
          onClick={() => setGroupMode(id)}
          className={`px-3 py-2 text-sm font-medium transition-colors inline-flex items-center gap-1.5 ${
            groupMode === id
              ? 'bg-blue-600 text-white'
              : 'bg-gray-900/40 text-gray-400 hover:text-gray-200 hover:bg-gray-800/60'
          }`}
        >
          {id === 'project' && <Layers size={13} />}
          {label}
        </button>
      ))}
    </div>
  );

  const toolbar = (full: boolean) => (
    <div className={`flex items-center gap-3 flex-wrap ${full ? '' : 'px-4 pb-3'}`}>
      <SearchInput
        value={search}
        onChange={setSearch}
        placeholder={
          moduleFilter
            ? t('containers.searchModule', 'hledat v kontejnerech modulu…')
            : t('containers.searchAll', 'hledat kontejner / image / modul / projekt…')
        }
        size='md'
        className='flex-1 min-w-[12rem]'
        resultCount={list.length}
        totalCount={all.length}
      />
      <div className='flex rounded-xl border border-gray-800 overflow-hidden'>
        {([
          ['all', t('containers.filterAll', 'Vše')],
          ['running', t('containers.running', 'Běžící')],
          ['stopped', t('containers.stopped', 'Zastavené')],
        ] as const).map(([id, label]) => (
          <button
            key={id}
            type='button'
            onClick={() => setFilter(id)}
            className={`px-3.5 py-2 text-sm font-medium transition-colors ${
              filter === id
                ? 'bg-blue-600 text-white'
                : 'bg-gray-900/40 text-gray-400 hover:text-gray-200 hover:bg-gray-800/60'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      {groupModeBar}
      <span className='text-sm text-gray-500 hidden sm:inline'>
        {t('containers.moduleProjectCount', '{{modules}} modulů · {{projects}} projektů', {
          modules: moduleCount,
          projects: projectCount,
        })}
      </span>
      <RefreshButton
        onRefresh={() => {
          q.refetch();
          locksQ.refetch();
        }}
        loading={q.isFetching}
        size='md'
      />
      {full && (
        <Button
          size='md'
          variant='primary'
          icon={<Plus size={14} />}
          onClick={() => setDeploy(true)}
        >
          Deploy
        </Button>
      )}
    </div>
  );

  const listBody = (
    <div className={isCompact ? '' : 'flex-1 min-h-0 overflow-auto'}>
      {q.isLoading
        ? (
          <div className='py-12 text-center text-sm text-gray-500 flex items-center justify-center gap-2'>
            <Loader2 size={16} className='animate-spin text-blue-400' />
            {t('containers.loadingMcp', 'Načítám přes MCP (docker.container_list)…')}
          </div>
        )
        : q.isError
        ? (
          <div className='m-4 rounded-xl border border-red-500/30 bg-red-500/5 px-4 py-3 text-sm text-red-300 space-y-2'>
            <p className='font-semibold'>{t('containers.loadListFailed', 'Načtení kontejnerů selhalo')}</p>
            <p className='text-xs font-mono text-red-400/90 break-all'>
              {(q.error as Error)?.message ?? t('containers.unknownError', 'neznámá chyba')}
            </p>
            <p className='text-xs text-gray-500'>
              {t('containers.mcpCallHint', 'Volání: POST /mcp → python proxy → {{tool}}. UI jen prezentuje výsledek.', { tool: T.list })}
            </p>
            <Button size='sm' variant='secondary' onClick={() => q.refetch()}>
              {t('widgets.retry', 'Zkusit znovu')}
            </Button>
          </div>
        )
        : list.length === 0
        ? (
          <EmptyState
            icon={Box}
            title={
              moduleFilter || prefixFilter
                ? t('containers.moduleEmpty', 'Modul nemá žádné kontejnery')
                : t('containers.noContainers', 'Žádné kontejnery')
            }
            description={search || filter !== 'all' ? t('containers.tryOtherFilter', 'Zkus jiný filtr nebo hledání.') : undefined}
          />
        )
        : !sectionOpen
        ? null
        : groups
          ? (
            <div className='space-y-5'>
              {groups.map((mod) => {
                const multi = mod.projects.length > 1;
                const modCollapseKey = `mod:${mod.moduleKey}`;
                const modOpen = !collapsedGroups.has(modCollapseKey);

                const renderProjectCard = (proj: ProjectGroup, showModuleBadge: boolean) => {
                  const gKey = `proj:${mod.moduleKey}/${proj.projectKey}`;
                  const open = !collapsedGroups.has(gKey);
                  const lockPanel = groupLockOpen === gKey;
                  const names = proj.rows.map((r) => clean(r.name));
                  const runN = proj.rows.filter((r) => r.state === 'running').length;
                  const lockedN = names.filter((n) => lockSet.has(n)).length;
                  const allLocked = lockedN === names.length && names.length > 0;
                  const unlocked = names.filter((n) => !lockSet.has(n));
                  // Title: projekt; pokud default (= module name), ukaž modul
                  const title = proj.projectLabel;

                  return (
                    <div
                      key={gKey}
                      className='rounded-2xl border border-gray-800 overflow-hidden bg-gray-900/20'
                    >
                      <div
                        className='flex items-center gap-3 px-4 py-3 bg-gray-900/60 border-b border-gray-800 cursor-pointer hover:bg-gray-900/80 transition-colors select-none'
                        onClick={() => toggleGroupCollapsed(gKey)}
                      >
                        {open
                          ? <ChevronDown size={16} className='text-gray-500 shrink-0' />
                          : <ChevronRight size={16} className='text-gray-500 shrink-0' />}
                        <span className='font-mono text-sm font-semibold text-white truncate'>
                          {title}
                        </span>
                        {/* Skupina slepená podle jména, ne podle identity — kontejnery
                            vznikly před zavedením acm.project. Ukázat to, ať se
                            seskupení nečte jako fakt. */}
                        {proj.projectKey !== '_other' && !projectHasIdentity(proj.rows) && (
                          <Badge variant='gray'>
                            <span
                              className='text-[10px]'
                              title={t('containers.noProjectIdHint', 'Kontejnery nemají label acm.project — seskupeno podle názvu, ID dostanou při dalším create/recreate')}
                            >
                              {t('containers.noProjectId', 'bez ID')}
                            </span>
                          </Badge>
                        )}
                        {showModuleBadge && mod.moduleKey !== '_other' && (
                          <Badge variant='gray'>
                            <span className='font-mono text-[10px]'>{mod.moduleLabel}</span>
                          </Badge>
                        )}
                        {proj.projectKey !== '_default' &&
                          proj.projectKey !== '_standalone' &&
                          !showModuleBadge &&
                          mod.moduleKey !== '_other' && (
                          <span className='text-[10px] uppercase tracking-wider text-gray-500 hidden sm:inline'>
                            {t('containers.projectWord', 'projekt')}
                          </span>
                        )}
                        <span
                          className={`text-xs font-mono tabular-nums ${
                            runN === proj.rows.length ? 'text-emerald-400' : 'text-amber-400'
                          }`}
                        >
                          {runN}/{proj.rows.length}
                        </span>
                        {lockedN > 0 && (
                          <Badge variant={allLocked ? 'warning' : 'gray'}>
                            <span className='inline-flex items-center gap-1'>
                              <LockIcon size={11} />
                              {lockedN}/{proj.rows.length}
                            </span>
                          </Badge>
                        )}

                        <div
                          className='ml-auto flex items-center gap-1.5'
                          onClick={(e) => e.stopPropagation()}
                        >
                          <button
                            type='button'
                            title={allLocked ? t('containers.groupLockedManage', 'Skupina zamčena — správa zámků') : t('containers.lockProject', 'Zamknout projekt')}
                            onClick={() =>
                              setGroupLockOpen((cur) => (cur === gKey ? null : gKey))}
                            className={`px-2.5 py-1.5 rounded-lg text-xs border transition-colors inline-flex items-center gap-1.5 ${
                              allLocked || lockPanel
                                ? 'bg-amber-500/10 border-amber-500/25 text-amber-400 hover:bg-amber-500/20'
                                : 'bg-gray-800/60 border-gray-700 text-gray-400 hover:text-amber-400 hover:border-amber-500/30'
                            }`}
                          >
                            <LockIcon size={13} />
                            <span className='hidden sm:inline'>
                              {allLocked ? t('containers.locks', 'Zámky') : t('containers.lock', 'Zamknout')}
                            </span>
                          </button>
                          {(['start', 'stop', 'restart'] as const).map((a) => (
                            <button
                              key={a}
                              type='button'
                              title={t('containers.actionWholeProject', '{{action}} celý projekt', { action: a })}
                              onClick={() => groupAction(names, a)}
                              className='px-2.5 py-1.5 rounded-lg text-xs border bg-gray-800/60 border-gray-700 text-gray-300 hover:text-white hover:bg-gray-700/60 transition-colors capitalize'
                            >
                              {a}
                            </button>
                          ))}
                        </div>
                      </div>

                      {lockPanel && (
                        <div className='px-4 py-3 bg-gray-950/50 border-b border-gray-800 space-y-2'>
                          <div className='flex items-center gap-2 text-sm text-gray-300 mb-1'>
                            <LockIcon size={14} className='text-amber-400' />
                            <span className='font-semibold'>{t('containers.groupLock', 'Skupinový zámek — {{id}}', { id: title })}</span>
                            <span className='text-gray-600'>{t('containers.containerCountParen', '({{count}} kontejnerů)', { count: proj.rows.length })}</span>
                          </div>
                          <GroupLockAll
                            names={unlocked}
                            onDone={() => locksQ.refetch()}
                          />
                          {names.map((n) => (
                            <LockRow
                              key={n}
                              containerId={n}
                              lock={lockMap.get(n) ?? null}
                              onChanged={() => locksQ.refetch()}
                            />
                          ))}
                        </div>
                      )}

                      {open && (
                        <>
                          {tableHead}
                          {proj.rows.map(renderRow)}
                        </>
                      )}
                    </div>
                  );
                };

                // Scoped moduleFilter + group by project: karty projektů bez zbytečného chrome
                if (moduleFilter && groupMode === 'project') {
                  // V project mode je top-level = projekt; sub = modul (jeden u scoped)
                  return (
                    <div key={mod.moduleKey} className='space-y-2'>
                      {renderProjectCard(
                        {
                          projectKey: mod.moduleKey,
                          projectLabel: mod.moduleLabel,
                          rows: mod.projects.flatMap((p) => p.rows),
                        },
                        false,
                      )}
                    </div>
                  );
                }
                if (moduleFilter && groupMode === 'module') {
                  return (
                    <div key={mod.moduleKey} className={multi ? 'space-y-2' : undefined}>
                      {mod.projects.map((p) => renderProjectCard(p, false))}
                    </div>
                  );
                }

                // Jeden pod-bucket → přímo karta (badge nadřazené úrovně)
                if (!multi) {
                  const only = mod.projects[0]!;
                  // U project mode: karta = celý projekt (všechny kontejnery)
                  if (groupMode === 'project') {
                    return (
                      <div key={mod.moduleKey}>
                        {renderProjectCard(
                          {
                            projectKey: mod.moduleKey,
                            projectLabel: mod.moduleLabel,
                            rows: only.rows,
                          },
                          false,
                        )}
                      </div>
                    );
                  }
                  return (
                    <div key={mod.moduleKey}>
                      {renderProjectCard(only, true)}
                    </div>
                  );
                }

                // Víc pod-skupin → hlavička (modul nebo projekt) + sub-karty
                const topKind = groupMode === 'project' ? t('containers.projectWord', 'projekt') : t('containers.moduleWord', 'modul');
                return (
                  <div key={mod.moduleKey} className='space-y-2'>
                    <div
                      className='flex items-center gap-3 px-1 cursor-pointer select-none group'
                      onClick={() => toggleGroupCollapsed(modCollapseKey)}
                    >
                      {modOpen
                        ? <ChevronDown size={14} className='text-gray-500' />
                        : <ChevronRight size={14} className='text-gray-500' />}
                      <span className='text-xs font-semibold uppercase tracking-widest text-gray-400 group-hover:text-gray-200 transition-colors'>
                        {mod.moduleLabel}
                      </span>
                      <span className='text-[10px] text-gray-600 uppercase tracking-wide'>
                        {topKind}
                      </span>
                      <span
                        className={`text-xs font-mono tabular-nums ${
                          mod.running === mod.total ? 'text-emerald-400' : 'text-amber-400'
                        }`}
                      >
                        {mod.running}/{mod.total}
                      </span>
                      <span className='text-xs text-gray-600'>
                        · {t('containers.projectCount', '{{count}} projektů', { count: mod.projects.length })}
                      </span>
                      <div
                        className='ml-auto flex items-center gap-1.5'
                        onClick={(e) => e.stopPropagation()}
                      >
                        {(['start', 'stop', 'restart'] as const).map((a) => (
                          <button
                            key={a}
                            type='button'
                            title={t('containers.actionWholeModule', '{{action}} celý modul', { action: a })}
                            onClick={() =>
                              groupAction(
                                mod.projects.flatMap((p) => p.rows.map((r) => clean(r.name))),
                                a,
                              )}
                            className='px-2 py-1 rounded-lg text-[11px] border bg-gray-800/40 border-gray-800 text-gray-500 hover:text-white hover:border-gray-600 transition-colors capitalize'
                          >
                            {a}
                          </button>
                        ))}
                      </div>
                    </div>
                    {modOpen && (
                      <div className='space-y-2 pl-0 sm:pl-2 border-l border-gray-800/60 sm:ml-1.5'>
                        {mod.projects.map((p) => renderProjectCard(p, false))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )
          : (
            <div className={`${isCompact ? '' : 'rounded-2xl border border-gray-800 overflow-hidden bg-gray-900/20'}`}>
              {tableHead}
              {list.map(renderRow)}
            </div>
          )}
    </div>
  );

  const modals = (
    <>
      {q.isError && (
        <div className='text-sm text-red-400 px-4 py-2'>
          {(q.error as Error)?.message ?? t('containers.loadError', 'Chyba načtení')}
        </div>
      )}
      {logs && <LogsModal name={logs} scope={scope} onClose={() => setLogs(null)} />}
      {inspect && <InspectModal name={inspect} scope={scope} onClose={() => setInspect(null)} />}
      {deploy && (
        <DeployModal
          scope={scope}
          onClose={() => setDeploy(false)}
          onDone={() => {
            setDeploy(false);
            q.refetch();
            qc.invalidateQueries({ queryKey: ['widget-data'] });
          }}
        />
      )}
    </>
  );

  // ── Compact embed (modulová patička / md widget) ──────────────────────────
  if (isCompact) {
    return (
      <div
        className={`rounded-2xl border border-gray-800 overflow-hidden bg-gray-900/20 ${className ?? ''}`}
      >
        <div
          className='flex items-center gap-3 px-4 py-3 border-b border-gray-800 cursor-pointer select-none hover:bg-gray-900/50 transition-colors'
          onClick={() => setSectionOpen((o) => !o)}
        >
          {sectionOpen
            ? <ChevronDown size={16} className='text-gray-500 shrink-0' />
            : <ChevronRight size={16} className='text-gray-500 shrink-0' />}
          <Box size={15} className='text-gray-400 shrink-0' />
          <span className='text-sm font-semibold text-white truncate'>{sectionTitle}</span>
          {!q.isLoading && (
            <span
              className={`text-xs font-mono tabular-nums ${
                list.length > 0 && running === list.length ? 'text-emerald-400' : 'text-amber-400'
              }`}
            >
              {running}/{list.length}
            </span>
          )}
          {projectCount > 1 && (
            <span className='text-xs text-gray-600 hidden sm:inline'>
              · {t('containers.projectCount', '{{count}} projektů', { count: projectCount })}
            </span>
          )}
          <div className='ml-auto' onClick={(e) => e.stopPropagation()}>
            <RefreshButton
              onRefresh={() => {
                q.refetch();
                locksQ.refetch();
              }}
              loading={q.isFetching}
              size='sm'
            />
          </div>
        </div>
        {sectionOpen && (
          <>
            {(list.length >= 3 || search || filter !== 'all' || projectCount > 1) && toolbar(false)}
            <div className='max-h-[32rem] overflow-auto'>
              {listBody}
            </div>
          </>
        )}
        {modals}
      </div>
    );
  }

  // ── Full page / widget ───────────────────────────────────────────────────
  return (
    <div className={`h-full flex flex-col gap-4 p-4 ${className ?? ''}`}>
      {showControls && (
        <>
          <StatsGrid columns={3}>
            <StatCard label={t('containers.total', 'Celkem')} value={list.length} icon={Box} />
            <StatCard label={t('containers.running', 'Běžící')} value={running} icon={CheckCircle2} variant='success' />
            <StatCard
              label={t('containers.stopped', 'Zastavené')}
              value={stopped}
              icon={Clock}
              variant={stopped > 0 ? 'warning' : 'default'}
            />
          </StatsGrid>
          {toolbar(true)}
        </>
      )}
      {listBody}
      {modals}
    </div>
  );
}

/**
 * Patička modulové stránky — kontejnery daného modulu.
 * Stejný widget jako /core/containers, jen scoped + compact.
 */
export function ModuleContainerList({
  module,
  scope = { node: null, identity: null },
  title,
}: {
  module: string;
  scope?: EffectiveScope;
  title?: string;
}) {
  return (
    <ContainerList
      scope={scope}
      moduleFilter={module}
      compact
      hideWhenEmpty
      title={title ?? i18n.t('containers.moduleTitle', 'Kontejnery · {{module}}', { module })}
    />
  );
}

// ── Images ───────────────────────────────────────────────────────────────────

function DockerImagesWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const q = useWidgetData<{
    ok?: boolean;
    images?: Array<{ id: string; repo: string; tag: string; size: string; created: string }>;
  }>(T.imageList, {}, scope, { refetchInterval: 30_000 });
  const [pull, setPull] = useState('');
  const pullM = useMutation({
    mutationFn: () =>
      callWidgetTool(T.imageAction, { action: 'pull', image: pull.trim() }, scope),
    onSuccess: (r) => {
      if ((r as { ok?: boolean }).ok === false) {
        toast.error(String((r as { error?: string }).error));
      } else toast.success(`Pull: ${pull}`);
      q.refetch();
      setPull('');
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const rmM = useMutation({
    mutationFn: (image: string) =>
      callWidgetTool(T.imageAction, { action: 'remove', image, force: true }, scope),
    onSuccess: () => {
      toast.success(t('containers.imageDeletedToast', 'Image smazán'));
      q.refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const images = q.data?.images ?? [];

  return (
    <div className='h-full flex flex-col gap-4 p-4'>
      <div className='flex items-center gap-3 flex-wrap'>
        <Input
          className='flex-1 min-w-[12rem] font-mono'
          placeholder='nginx:alpine'
          value={pull}
          onChange={(e) => setPull(e.target.value)}
          onKeyDown={(e) => e.key === 'Enter' && pull.trim() && pullM.mutate()}
        />
        <Button
          size='md'
          variant='primary'
          loading={pullM.isPending}
          disabled={!pull.trim()}
          onClick={() => pullM.mutate()}
        >
          Pull
        </Button>
        <span className='text-sm text-gray-500'>{images.length} images</span>
        <RefreshButton onRefresh={() => q.refetch()} loading={q.isFetching} size='md' />
      </div>
      <div className='flex-1 min-h-0 overflow-auto rounded-2xl border border-gray-800 bg-gray-900/20'>
        {images.length === 0 && !q.isLoading
          ? <EmptyState icon={ImageIcon} title={t('containers.noImages', 'Žádné images')} />
          : images.map((img) => (
            <div
              key={`${img.id}-${img.repo}-${img.tag}`}
              className='flex items-center gap-3 px-4 py-3 border-b border-gray-800/50 last:border-0 hover:bg-gray-800/20'
            >
              <span className='font-mono text-sm text-gray-200 truncate flex-1'>
                {img.repo}:{img.tag}
              </span>
              <span className='text-xs text-gray-500 font-mono shrink-0'>{img.size}</span>
              <span className='text-xs text-gray-600 shrink-0 hidden sm:inline'>{img.created}</span>
              <IconBtn
                icon={<Trash2 size={13} />}
                title={t('common.delete', 'Smazat')}
                variant='danger'
                onClick={async () => {
                  if (await themedConfirm(t('containers.confirmDeleteImage', 'Smazat {{repo}}:{{tag}}?', { repo: img.repo, tag: img.tag }))) rmM.mutate(img.id);
                }}
              />
            </div>
          ))}
      </div>
    </div>
  );
}

// ── Volumes ──────────────────────────────────────────────────────────────────

function DockerVolumesWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const q = useWidgetData<{ ok?: boolean; volumes?: Array<{ name: string; driver: string }> }>(
    T.volumeList,
    {},
    scope,
    { refetchInterval: 30_000 },
  );
  const rmM = useMutation({
    mutationFn: (name: string) =>
      callWidgetTool(T.volumeAction, { action: 'remove', name }, scope),
    onSuccess: () => {
      toast.success(t('containers.volumeDeletedToast', 'Volume smazán'));
      q.refetch();
    },
    onError: (e) => toast.error((e as Error).message),
  });
  const volumes = q.data?.volumes ?? [];

  return (
    <div className='h-full flex flex-col gap-4 p-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm text-gray-500'>{volumes.length} volumes</span>
        <RefreshButton onRefresh={() => q.refetch()} loading={q.isFetching} size='md' />
      </div>
      <div className='flex-1 min-h-0 overflow-auto rounded-2xl border border-gray-800 bg-gray-900/20'>
        {volumes.length === 0 && !q.isLoading
          ? <EmptyState icon={HardDrive} title={t('containers.noVolumes', 'Žádné volumes')} />
          : volumes.map((v) => (
            <div
              key={v.name}
              className='flex items-center gap-3 px-4 py-3 border-b border-gray-800/50 last:border-0 hover:bg-gray-800/20'
            >
              <HardDrive size={16} className='text-gray-500 shrink-0' />
              <span className='font-mono text-sm text-gray-200 truncate flex-1'>{v.name}</span>
              <Badge variant='gray'>{v.driver}</Badge>
              <IconBtn
                icon={<Trash2 size={13} />}
                title={t('common.delete', 'Smazat')}
                variant='danger'
                onClick={async () => {
                  if (await themedConfirm(t('containers.confirmDeleteVolume', "Smazat volume '{{name}}'?", { name: v.name }))) rmM.mutate(v.name);
                }}
              />
            </div>
          ))}
      </div>
    </div>
  );
}

// ── Networks ─────────────────────────────────────────────────────────────────

function DockerNetworksWidget({ scope }: WidgetRenderProps) {
  const { t } = useTranslation();
  const q = useWidgetData<{
    ok?: boolean;
    networks?: Array<{
      id: string;
      name: string;
      driver: string;
      containers?: Array<{ name: string; ipv4: string }>;
    }>;
  }>(T.networkList, {}, scope, { refetchInterval: 30_000 });
  const nets = q.data?.networks ?? [];

  return (
    <div className='h-full flex flex-col gap-4 p-4'>
      <div className='flex items-center justify-between'>
        <span className='text-sm text-gray-500'>{t('containers.networkCount', '{{count}} sítí', { count: nets.length })}</span>
        <RefreshButton onRefresh={() => q.refetch()} loading={q.isFetching} size='md' />
      </div>
      <div className='flex-1 min-h-0 overflow-auto space-y-3'>
        {nets.length === 0 && !q.isLoading
          ? <EmptyState icon={Network} title={t('containers.noNetworks', 'Žádné sítě')} />
          : nets.map((n) => (
            <div
              key={n.id || n.name}
              className='rounded-2xl border border-gray-800 bg-gray-900/20 px-4 py-3'
            >
              <div className='flex items-center gap-3'>
                <Network size={16} className='text-gray-500' />
                <span className='font-mono text-sm text-white'>{n.name}</span>
                <Badge variant='gray'>{n.driver}</Badge>
                <span className='ml-auto text-xs text-gray-500'>
                  {t('containers.containerCountN', '{{count}} kontejnerů', { count: (n.containers ?? []).length })}
                </span>
              </div>
              {(n.containers?.length ?? 0) > 0 && (
                <div className='mt-2 pl-7 space-y-1'>
                  {n.containers!.map((c) => (
                    <div key={c.name} className='text-xs font-mono text-gray-500'>
                      {c.name}
                      <span className='text-gray-700'> · </span>
                      {c.ipv4 || '—'}
                    </div>
                  ))}
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
}

// ── Widget shells ────────────────────────────────────────────────────────────

function DockerContainersWidget({ scope, format }: WidgetRenderProps) {
  // lg = plná stránka Kontejnery; md = compact karta (dashboard panel / embed)
  return (
    <ContainerList
      scope={scope}
      controls={format !== 'md'}
      compact={format === 'md'}
    />
  );
}

export const DOCKER_WIDGETS: WidgetSpec[] = [
  {
    id: 'docker.containers',
    title: 'Containers',
    module: 'docker',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Box,
    since: '2.1.0',
    updated: '2026-07-26',
    component: DockerContainersWidget,
  },
  {
    id: 'docker.images',
    title: 'Images',
    module: 'docker',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: ImageIcon,
    since: '2.1.0',
    updated: '2026-07-26',
    component: DockerImagesWidget,
  },
  {
    id: 'docker.volumes',
    title: 'Volumes',
    module: 'docker',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: HardDrive,
    since: '2.1.0',
    updated: '2026-07-26',
    component: DockerVolumesWidget,
  },
  {
    id: 'docker.networks',
    title: 'Networks',
    module: 'docker',
    kind: 'custom',
    format: 'lg',
    formats: ['md', 'lg'],
    icon: Network,
    since: '2.1.0',
    updated: '2026-07-26',
    component: DockerNetworksWidget,
  },
];

